/**
 * PresenceBubbles — live avatar stack of users on the same page.
 * Each bubble shows the user's initials on their assigned color.
 * Self is hidden (we know we're here).
 */
import React from "react";
import { useAuth } from "../lib/auth";

const MAX_VISIBLE = 4;

export default function PresenceBubbles({ presence, className = "" }) {
  const { user } = useAuth();
  if (!presence || presence.length === 0) return null;
  const others = presence.filter((p) => p.id !== user?.id);
  if (others.length === 0) {
    return (
      <div className={`flex items-center gap-2 text-[10px] text-emerald-400 font-mono uppercase tracking-wider ${className}`}
        data-testid="presence-self-only">
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
        Live
      </div>
    );
  }
  const visible = others.slice(0, MAX_VISIBLE);
  const overflow = others.length - visible.length;
  return (
    <div className={`flex items-center gap-2 ${className}`} data-testid="presence-bubbles">
      <span className="text-[10px] text-emerald-400 font-mono uppercase tracking-wider hidden sm:inline">
        <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse mr-1.5 align-middle" />
        Live
      </span>
      <div className="flex -space-x-2">
        {visible.map((u) => (
          <div
            key={u.id}
            title={`${u.name}${u.role ? ` · ${u.role}` : ""}${u.connections > 1 ? ` · ${u.connections} tabs` : ""}`}
            className="relative w-7 h-7 rounded-full ring-2 ring-[#0A0A0A] flex items-center justify-center text-[10px] font-semibold text-white shadow-sm transition-transform hover:scale-110 hover:z-10"
            style={{ background: u.color || "#6B7280" }}
            data-testid={`presence-bubble-${u.id}`}
          >
            {u.initials || (u.name || "?")[0]?.toUpperCase()}
          </div>
        ))}
        {overflow > 0 && (
          <div className="w-7 h-7 rounded-full ring-2 ring-[#0A0A0A] bg-zinc-800 text-zinc-300 flex items-center justify-center text-[10px] font-semibold"
            title={others.slice(MAX_VISIBLE).map((u) => u.name).join(", ")}
            data-testid="presence-overflow"
          >
            +{overflow}
          </div>
        )}
      </div>
    </div>
  );
}
